// Phase 3: turn per-node style copies into shared records without changing a
// single resolved style. Role base styles take each property's most common
// value; recurring combinations of differences become named text styles;
// anything left is a sparse per-element override.
const { resolveStyle } = require("./styles");
const { deepEqual } = require("./docOps");

const ROLE_NAMES = {
  title: "Restaurant title", subtitle: "Menu subtitle", section: "Section headings", dish: "Dish names",
  description: "Descriptions", price: "Prices", note: "Notes", decoration: "Artwork",
};
const stable = (value) => JSON.stringify(value && typeof value === "object" ? Object.fromEntries(Object.keys(value).sort().map((k) => [k, value[k]])) : value);

function mode(values) {
  const counts = new Map();
  values.forEach((v) => { const k = stable(v); counts.set(k, { v, n: ((counts.get(k) || {}).n || 0) + 1 }); });
  return [...counts.values()].sort((a, b) => b.n - a.n)[0].v;
}

function describeStyle(style) {
  const parts = [];
  if (style.fontFamily) parts.push(String(style.fontFamily).split(",")[0].replace(/"/g, "").trim());
  if (style.fontSize !== undefined) parts.push(`${Math.round(style.fontSize * 100) / 100}pt`);
  if (style.fontWeight !== undefined) parts.push(String(style.fontWeight));
  if (style.fontStyle === "italic") parts.push("italic");
  if (style.align) parts.push(style.align);
  const rest = Object.keys(style).filter((k) => !["fontFamily", "fontSize", "fontWeight", "fontStyle", "align"].includes(k));
  const label = { lineHeight: "leading", letterSpacing: "tracking", textTransform: "case", fill: "colour", fontVariant: "figures", fontFeatureSettings: "features" };
  const fmt = (v) => (typeof v === "number" ? Math.round(v * 100) / 100 : String(v).replace(/"/g, ""));
  rest.slice(0, 2).forEach((k) => parts.push(`${label[k] || k} ${fmt(style[k])}`));
  if (rest.length > 2) parts.push(`+${rest.length - 2}`);
  return parts.join(" · ") || "variant";
}

// options.minCluster: smallest group of identical differences that becomes a
// named style (default 3). options.slug: id prefix.
function normalizeStyles(doc, options = {}) {
  const minCluster = options.minCluster || 3;
  const nodes = doc.pages.flatMap((p) => p.elements);
  const before = new Map(nodes.map((n) => [n.id, resolveStyle(doc, n)]));
  const styles = { ...doc.styles };
  const textStyles = { ...(doc.textStyles || {}) };
  const nodeStyle = new Map(), nodeRef = new Map();
  const report = { roles: {}, textStylesCreated: 0, overrideKeysBefore: 0, overrideKeysAfter: 0 };
  nodes.forEach((n) => { report.overrideKeysBefore += Object.keys(n.style || {}).length; });
  const existingRefs = nodes.some((n) => n.styleRef);
  if (existingRefs) throw new Error("This design already uses named text styles; normalization runs once on legacy styling.");

  for (const role of Object.keys(styles)) {
    const members = nodes.filter((n) => n.role === role);
    if (!members.length) continue;
    const effective = members.map((n) => before.get(n.id));
    // Only properties every member resolves can move into the base; otherwise
    // a member that lacks the property would start inheriting it.
    const shared = Object.keys(effective[0]).filter((k) => effective.every((e) => Object.prototype.hasOwnProperty.call(e, k) && e[k] !== undefined));
    const base = { ...styles[role] };
    shared.forEach((k) => { base[k] = mode(effective.map((e) => e[k])); });
    styles[role] = base;
    const diffs = members.map((n, i) => {
      const e = effective[i], d = {};
      for (const [k, v] of Object.entries(e)) if (v !== undefined && !deepEqual(base[k], v)) d[k] = v;
      return d;
    });
    const clusters = new Map();
    diffs.forEach((d, i) => {
      if (!Object.keys(d).length) return;
      const key = stable(d);
      if (!clusters.has(key)) clusters.set(key, { style: d, members: [] });
      clusters.get(key).members.push(members[i]);
    });
    let index = 0;
    const named = [];
    for (const cluster of [...clusters.values()].sort((a, b) => b.members.length - a.members.length)) {
      if (cluster.members.length < minCluster || members[0].type === "image") continue;
      const id = `ts:${options.slug || ""}${role}-${++index}`;
      const baseName = `${ROLE_NAMES[role] || role} · ${describeStyle(cluster.style)}`;
      const taken = new Set(Object.values(textStyles).map((t) => t.name));
      let name = baseName, n = 2;
      while (taken.has(name)) name = `${baseName} (${n++})`;
      textStyles[id] = { name, role, style: cluster.style };
      cluster.members.forEach((n) => { nodeRef.set(n.id, id); nodeStyle.set(n.id, {}); });
      named.push({ id, members: cluster.members.length });
      report.textStylesCreated++;
    }
    members.forEach((n, i) => { if (!nodeRef.has(n.id)) nodeStyle.set(n.id, diffs[i]); });
    report.roles[role] = { members: members.length, base: Object.keys(base).length, namedStyles: named, individual: members.filter((n) => !nodeRef.has(n.id) && Object.keys(nodeStyle.get(n.id)).length).length };
  }

  const next = {
    ...doc, styles, textStyles,
    pages: doc.pages.map((page) => ({ ...page, elements: page.elements.map((n) => {
      if (!nodeStyle.has(n.id)) return n;
      const out = { ...n, style: nodeStyle.get(n.id) };
      if (nodeRef.has(n.id)) out.styleRef = nodeRef.get(n.id);
      return out;
    }) })),
  };
  // Gate: every element must resolve exactly as before.
  const changed = [];
  for (const page of next.pages) for (const n of page.elements) if (!deepEqual(resolveStyle(next, n), before.get(n.id))) changed.push(n.id);
  if (changed.length) throw new Error(`Normalization would change ${changed.length} element styles; aborted.`);
  next.pages.forEach((p) => p.elements.forEach((n) => { report.overrideKeysAfter += Object.keys(n.style || {}).length; }));
  if (!Object.keys(textStyles).length) delete next.textStyles;
  return { doc: next, report };
}

// Values that differ from a round number by tiny amounts (import measurement
// noise). Reported only: rounding changes output and needs explicit approval.
function measurementNoise(doc) {
  const hits = [];
  const check = (where, key, value) => {
    if (!Number.isFinite(value)) return;
    const rounded = Math.round(value * 4) / 4;
    if (value !== rounded && Math.abs(value - rounded) < 0.02) hits.push({ where, key, value, rounded });
  };
  Object.entries(doc.styles || {}).forEach(([role, s]) => Object.entries(s).forEach(([k, v]) => check(`role:${role}`, k, v)));
  Object.entries(doc.textStyles || {}).forEach(([id, s]) => Object.entries(s.style).forEach(([k, v]) => check(id, k, v)));
  doc.pages.forEach((p) => p.elements.forEach((n) => Object.entries(n.style || {}).forEach(([k, v]) => check(`node:${n.id}`, k, v))));
  return hits;
}

module.exports = { normalizeStyles, measurementNoise, describeStyle };
